import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/SEO';
import { FileText, Download, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('uploadedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/documents?sort_by=${sortBy}&order=${sortOrder}`);
        if (response.ok) {
          const data = await response.json();
          setDocuments(data);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch documents:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [sortBy, sortOrder]);

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.description && doc.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('sheet') || fileType.includes('excel')) return '📊';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📽️';
    return '📎';
  };

  const getFileTypeLabel = (fileType) => {
    if (fileType.includes('pdf')) return 'PDF';
    if (fileType.includes('word') || fileType.includes('document')) return 'Word';
    if (fileType.includes('sheet') || fileType.includes('excel')) return 'Excel';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return 'PowerPoint';
    return 'Document';
  };

  return (
    <Layout>
      <SEO 
        title="Documents & Downloads"
        description="Access church bulletins, forms, newsletters, and other documents from Geneva United Methodist Church."
        url="/documents"
      />
      
      {/* Hero Section */}
      <section className="relative bg-primary-900 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-primary-900/90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-secondary font-medium mb-4">Resources</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Documents & Downloads
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Access bulletins, forms, newsletters, and other church documents
          </p>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-16 bg-warm-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Sort Controls */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-8">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  data-testid="document-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-secondary"
                >
                  <option value="uploadedAt">Date</option>
                  <option value="title">Title</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
                >
                  {sortOrder === 'asc' ? '↑ Oldest' : '↓ Newest'}
                </button>
              </div>
            </div>
          </div>

          {/* Documents List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-500">Loading documents...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-600 mb-2">
                {searchTerm ? 'No documents found' : 'No documents available'}
              </h3>
              <p className="text-slate-500">
                {searchTerm ? 'Try adjusting your search terms.' : 'Check back later for church documents and resources.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDocuments.map((doc) => (
                <a
                  key={doc.id}
                  href={`${API_URL}${doc.fileUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md hover:border-secondary/30 transition-all group"
                  data-testid={`document-${doc.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{getFileIcon(doc.fileType)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-heading font-semibold text-primary-900 group-hover:text-secondary transition-colors">
                            {doc.title}
                          </h3>
                          {doc.description && (
                            <p className="text-slate-600 mt-1">{doc.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span>{getFileTypeLabel(doc.fileType)}</span>
                            <span>•</span>
                            <span>{formatFileSize(doc.fileSize)}</span>
                            <span>•</span>
                            <span>{formatDate(doc.uploadedAt)}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center group-hover:bg-secondary group-hover:text-white transition-colors">
                            <Download className="w-5 h-5 text-secondary group-hover:text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Document Count */}
          {!loading && filteredDocuments.length > 0 && (
            <p className="text-center text-slate-500 mt-8">
              Showing {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Documents;
